import { useEffect, useRef } from 'react'

import { SUPPORTED_NON_DEFAULT_LOCALE_PREFIXES } from 'Pods/Shared/Utils/getLocaleParam'

import { getListingPagePaths } from 'Utils/getListingPagePaths'

const DEFAULT_LISTING_PATH = '3d-models'

function getListingBasePath() {
  if (typeof window === 'undefined') {
    return DEFAULT_LISTING_PATH
  }

  const pathSegments = window.location.pathname.split('/').filter(Boolean)

  if (!pathSegments.length) {
    return DEFAULT_LISTING_PATH
  }

  const currentLocale = window.I18n?.locale?.toLowerCase()
  const defaultLocale = window.I18n?.defaultLocale?.toLowerCase()
  const firstSegment = pathSegments[0].toLowerCase()
  const hasExplicitLocale = currentLocale
    && defaultLocale
    && currentLocale !== defaultLocale
    && firstSegment === currentLocale

  if (hasExplicitLocale && pathSegments[1]) {
    return pathSegments[1]
  }

  const FALLBACK_LOCALE_PREFIXES = SUPPORTED_NON_DEFAULT_LOCALE_PREFIXES
  if (FALLBACK_LOCALE_PREFIXES.includes(firstSegment) && pathSegments[1]) {
    return pathSegments[1]
  }

  if (FALLBACK_LOCALE_PREFIXES.includes(firstSegment)) {
    return DEFAULT_LISTING_PATH
  }

  return pathSegments[0] || DEFAULT_LISTING_PATH
}

function useCategoryNavigation({
  isLoading,
  selectedFilters,
  handleFilterChange,
}) {
  const isNavigatingRef = useRef(false)

  useEffect(() => {
    if (!isLoading && isNavigatingRef.current) {
      isNavigatingRef.current = false
    }
  }, [isLoading])

  useEffect(() => {
    function handleCategoryNavigation(event) {
      if (isNavigatingRef.current || isLoading) {
        return
      }

      const { href } = event.detail
      if (!href) {
        return
      }

      const normalizedHref = href.startsWith('/') ? href : `/${href}`
      const hrefSegments = normalizedHref.split('/').filter(Boolean)

      let categorySlug = null
      let subcategorySlug = null
      let targetListingPath = null

      const localePrefixes = ['de', 'es', 'fr', 'pt', 'ru', 'ja', 'zh-cn']
      const firstSegment = hrefSegments[0]?.toLowerCase()
      const hasLocalePrefix = firstSegment && localePrefixes.includes(firstSegment)

      const defaultPaths = getListingPagePaths(null)
      const localizedPaths = hasLocalePrefix ? getListingPagePaths(firstSegment) : null

      // Check if first segment matches default paths (no locale prefix)
      if (firstSegment === defaultPaths.modelsPath) {
        targetListingPath = defaultPaths.modelsPath
        categorySlug = hrefSegments[1]
        subcategorySlug = hrefSegments[2] || null
      } else if (firstSegment === defaultPaths.printModelsPath) {
        targetListingPath = defaultPaths.printModelsPath
        categorySlug = hrefSegments[1]
        subcategorySlug = hrefSegments[2] || null
      } else if (hasLocalePrefix) {
        // Check if second segment matches localized paths (with locale prefix)
        if (localizedPaths && hrefSegments[1] === localizedPaths.modelsPath) {
          targetListingPath = localizedPaths.modelsPath
          categorySlug = hrefSegments[2]
          subcategorySlug = hrefSegments[3] || null
        } else if (localizedPaths && hrefSegments[1] === localizedPaths.printModelsPath) {
          targetListingPath = localizedPaths.printModelsPath
          categorySlug = hrefSegments[2]
          subcategorySlug = hrefSegments[3] || null
        } else if (hrefSegments[1] === defaultPaths.modelsPath) {
          // Also check for default paths with locale prefix
          targetListingPath = defaultPaths.modelsPath
          categorySlug = hrefSegments[2]
          subcategorySlug = hrefSegments[3] || null
        } else if (hrefSegments[1] === defaultPaths.printModelsPath) {
          // Also check for default paths with locale prefix
          targetListingPath = defaultPaths.printModelsPath
          categorySlug = hrefSegments[2]
          subcategorySlug = hrefSegments[3] || null
        }
      }

      if (!categorySlug || !targetListingPath) {
        return
      }

      isNavigatingRef.current = true

      const currentListingPath = getListingBasePath()
      const isSwitchingListingType = targetListingPath !== currentListingPath

      const currentModelType = selectedFilters.modelType || {}
      let updatedModelType = { ...currentModelType }

      if (isSwitchingListingType) {
        const printModelsPaths = [defaultPaths.printModelsPath]
        const modelsPaths = [defaultPaths.modelsPath]
        if (localizedPaths) {
          printModelsPaths.push(localizedPaths.printModelsPath)
          modelsPaths.push(localizedPaths.modelsPath)
        }
        if (printModelsPaths.includes(targetListingPath)) {
          // eslint-disable-next-line camelcase
          updatedModelType = { ...currentModelType, print_ready: 1 }
        } else if (modelsPaths.includes(targetListingPath)) {
          // eslint-disable-next-line camelcase
          const { print_ready, ...restModelType } = currentModelType
          updatedModelType = restModelType
        }
      }

      const newFiltersObject = {
        ...selectedFilters,
        parent_category_slug: categorySlug,
        subcategory_slug: subcategorySlug || undefined,
        page: undefined,
        ...(Object.keys(updatedModelType).length > 0 && {
          modelType: updatedModelType,
        }),
      }

      handleFilterChange({
        name: 'parent_category_slug',
        value: categorySlug,
        newObject: newFiltersObject,
        skipGetLastSelectedFilter: true,
        customListingPath: isSwitchingListingType ? targetListingPath : undefined,
      })
    }

    window.addEventListener('listing-page:navigate-category', handleCategoryNavigation)

    return () => {
      window.removeEventListener('listing-page:navigate-category', handleCategoryNavigation)
    }
  }, [selectedFilters, handleFilterChange, isLoading])
}

export default useCategoryNavigation
